import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Checkbox, Input, Select } from '@/components/ui/Field'
import { DataRow } from '@/components/ui/Page'
import { cn } from '@/lib/cn'
import { formatDateShort, today } from '@/lib/dates'
import { deliverableDue, isDelivered, nextStep, queueProgress } from '@/lib/deliverables'
import { usePackages } from '@/features/quotes/queries'
import {
  useAddDeliverables,
  useAddStep,
  useJobDeliverables,
  useRemoveDeliverable,
  useRemoveStep,
  useSetDelivered,
  useToggleDeliverable,
  useToggleStep,
  type Deliverable,
} from './queries'

export const DELIVERABLE_KIND_LABEL: Record<Deliverable['kind'], string> = {
  photo: 'Photo',
  film: 'Film',
  album: 'Album',
  other: 'Other',
}

/**
 * What was promised, ticked off as it goes out: her Trello "Deliverables"
 * checklist ("35 sneak peeks", "800+ edited photos", "4–6 minute film", "A4
 * album"). Filled from the collection when the quote is accepted; anything
 * extra agreed with the couple is added here.
 *
 * Each deliverable carries its checkpoints (Culling done, Edited, Delivered
 * to the couple, Backed up), and "Files and backups" comes first: the photo
 * files and video footage, uploaded, backed up and archived before editing
 * starts. Defaults come from the database (default_deliverable_steps); each
 * wedding can add or remove its own.
 */
export function JobDeliverables({ jobId, packageId }: { jobId: string; packageId: string | null }) {
  const query = useJobDeliverables(jobId)
  const packages = usePackages(true)
  const add = useAddDeliverables(jobId)
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState<Deliverable['kind']>('photo')

  const items = query.data ?? []
  const files = items.filter((item) => item.internal)
  const promised = items.filter((item) => !item.internal)
  const done = promised.filter((item) => item.done_at).length
  const collection = packages.data?.find((item) => item.id === packageId) ?? null
  const template = Array.isArray(collection?.deliverables) ? (collection.deliverables as Array<{ title: string; kind: Deliverable['kind'] }>) : []

  function submit() {
    const trimmed = title.trim()
    if (!trimmed) return
    add.mutate([{ title: trimmed, kind, sort_order: items.length }])
    setTitle('')
  }

  return (
    <div className="flex flex-col gap-3">
      {query.isPending ? (
        <p className="text-[length:var(--text-2xs)] text-taupe">Loading…</p>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-start gap-2">
          <p className="text-[length:var(--text-sm)] text-taupe">Nothing listed yet.</p>
          {template.length > 0 ? (
            <Button
              size="sm"
              onClick={() => add.mutate(template.map((item, index) => ({ title: item.title, kind: item.kind, sort_order: index })))}
            >
              Fill from {collection?.name}
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          {files.length > 0 ? (
            <div>
              <p className="label">Files and backups</p>
              <ul className="flex flex-col">
                {files.map((item) => (
                  <DeliverableRow key={item.id} jobId={jobId} item={item} />
                ))}
              </ul>
            </div>
          ) : null}
          {promised.length > 0 ? (
            <div>
              <p className="label">
                For the couple · {done} of {promised.length} delivered
              </p>
              <ul className="flex flex-col">
                {promised.map((item) => (
                  <DeliverableRow key={item.id} jobId={jobId} item={item} />
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}

      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <Input aria-label="Deliverable" placeholder="e.g. A4 album" value={title} onChange={(event) => setTitle(event.target.value)} className="flex-1" />
        <Select aria-label="Kind" value={kind} onChange={(event) => setKind(event.target.value as Deliverable['kind'])} className="w-24">
          {(Object.keys(DELIVERABLE_KIND_LABEL) as Array<Deliverable['kind']>).map((key) => (
            <option key={key} value={key}>
              {DELIVERABLE_KIND_LABEL[key]}
            </option>
          ))}
        </Select>
        <Button type="submit" size="sm" disabled={!title.trim()}>
          Add
        </Button>
      </form>
    </div>
  )
}

function DeliverableRow({ jobId, item }: { jobId: string; item: Deliverable }) {
  const toggle = useToggleDeliverable(jobId)
  const toggleStep = useToggleStep(jobId)
  const addStep = useAddStep(jobId)
  const removeStep = useRemoveStep(jobId)
  const remove = useRemoveDeliverable(jobId)
  const [editing, setEditing] = useState(false)
  const [stepTitle, setStepTitle] = useState('')
  const ticked = item.steps.filter((step) => step.done_at).length

  return (
    <li className="border-b py-2">
      <div className="flex items-center justify-between gap-2">
        {item.steps.length === 0 ? (
          <Checkbox
            label={item.title}
            description={DELIVERABLE_KIND_LABEL[item.kind]}
            checked={Boolean(item.done_at)}
            onChange={(event) => toggle.mutate({ id: item.id, done: event.target.checked })}
          />
        ) : (
          <p className={cn('text-[length:var(--text-sm)]', item.done_at && 'text-sage')}>
            {item.title}{' '}
            <span className="text-[length:var(--text-2xs)] text-taupe">
              {DELIVERABLE_KIND_LABEL[item.kind]} · {ticked} of {item.steps.length}
              {item.done_at ? ' · done' : ''}
            </span>
          </p>
        )}
        <div className="flex shrink-0 items-center">
          <Button variant="ghost" size="sm" onClick={() => setEditing(!editing)}>
            {editing ? 'Done' : 'Steps'}
          </Button>
          {editing ? (
            <Button variant="ghost" size="sm" aria-label={`Remove ${item.title}`} onClick={() => remove.mutate(item.id)}>
              Remove
            </Button>
          ) : null}
        </div>
      </div>

      {item.steps.length > 0 || editing ? (
        <div className="mt-1.5 ml-1 flex flex-wrap items-center gap-x-5 gap-y-1.5 border-l border-l-[var(--border-hairline)] pl-3">
          {item.steps.map((step) => (
            <span key={step.id} className="flex items-center gap-1">
              <Checkbox
                label={step.title}
                checked={Boolean(step.done_at)}
                onChange={(event) => toggleStep.mutate({ id: step.id, done: event.target.checked })}
              />
              {editing ? (
                <button
                  type="button"
                  aria-label={`Remove the step ${step.title}`}
                  className="px-1 text-taupe hover:text-clay"
                  onClick={() => removeStep.mutate(step.id)}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
          {editing ? (
            <form
              className="flex items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault()
                const trimmed = stepTitle.trim()
                if (!trimmed) return
                addStep.mutate({
                  deliverable_id: item.id,
                  title: trimmed,
                  sort_order: (item.steps.at(-1)?.sort_order ?? -1) + 1,
                })
                setStepTitle('')
              }}
            >
              <Input
                aria-label="New step"
                placeholder="Add a step"
                value={stepTitle}
                onChange={(event) => setStepTitle(event.target.value)}
                className="w-40"
              />
              <Button type="submit" size="sm" variant="ghost" disabled={!stepTitle.trim()}>
                Add
              </Button>
            </form>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

/** Where the editing is, for "At a glance": the next step per queue. */
export function EditingAtAGlance({ jobId }: { jobId: string }) {
  const items = useJobDeliverables(jobId).data ?? []
  return (
    <>
      {(['photos', 'film'] as const).map((queue) => {
        const progress = queueProgress(items, queue)
        if (progress.total === 0) return null
        const next = nextStep(items, queue)
        return (
          <DataRow key={queue} label={queue === 'photos' ? 'Photos' : 'Film'}>
            {next ? (
              <>
                {next.step ? `${next.step} · ${next.item}` : next.item}{' '}
                <span className="text-taupe">
                  ({progress.done}/{progress.total})
                </span>
              </>
            ) : (
              <span className="text-sage">All ticked</span>
            )}
          </DataRow>
        )
      })}
    </>
  )
}

/**
 * The project page's deliverables (28 Oct 2026): only what the couple
 * receives, as a checklist. Ticking one marks it delivered, the same as the
 * Editing page and the calendar. The editing steps and "Files and backups"
 * stay on the Editing page; they move to a Workflow section later.
 */
export function ClientDeliverables({
  jobId,
  packageId,
  job,
}: {
  jobId: string
  packageId: string | null
  job: { event_date: string; photos_due_date: string | null; film_due_date: string | null }
}) {
  const query = useJobDeliverables(jobId)
  const packages = usePackages(true)
  const add = useAddDeliverables(jobId)
  const remove = useRemoveDeliverable(jobId)
  const setDelivered = useSetDelivered()
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState<Deliverable['kind']>('photo')

  const items = query.data ?? []
  const promised = items.filter((item) => !item.internal)
  const done = promised.filter(isDelivered).length
  const collection = packages.data?.find((item) => item.id === packageId) ?? null
  const template = Array.isArray(collection?.deliverables) ? (collection.deliverables as Array<{ title: string; kind: Deliverable['kind'] }>) : []
  const now = today()

  function submit() {
    const trimmed = title.trim()
    if (!trimmed) return
    add.mutate([{ title: trimmed, kind, sort_order: items.length }])
    setTitle('')
  }

  return (
    <div className="flex flex-col gap-3">
      {query.isPending ? (
        <p className="text-[length:var(--text-2xs)] text-taupe">Loading…</p>
      ) : promised.length === 0 ? (
        <div className="flex flex-col items-start gap-2">
          <p className="text-[length:var(--text-sm)] text-taupe">Nothing listed yet.</p>
          {template.length > 0 ? (
            <Button size="sm" onClick={() => add.mutate(template.map((item, index) => ({ title: item.title, kind: item.kind, sort_order: index })))}>
              Fill from {collection?.name}
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <p className="label">
            {done} of {promised.length} delivered
          </p>
          <ul className="flex flex-col">
            {promised.map((item) => {
              const delivered = isDelivered(item)
              const due = deliverableDue(item, job)
              return (
                <li key={item.id} className="group flex items-center justify-between gap-3 border-b py-2">
                  <Checkbox
                    label={item.title}
                    description={[
                      DELIVERABLE_KIND_LABEL[item.kind],
                      delivered ? 'delivered' : due ? `due ${formatDateShort(due)}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    checked={delivered}
                    disabled={setDelivered.isPending}
                    className={cn(delivered && '[&_label]:text-taupe [&_label]:line-through', !delivered && due && due < now && '[&_p]:text-clay')}
                    onChange={(event) => setDelivered.mutate({ jobId, item, delivered: event.target.checked })}
                  />
                  <button
                    type="button"
                    aria-label={`Remove ${item.title}`}
                    className="label opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-clay"
                    onClick={() => {
                      if (window.confirm(`Remove "${item.title}" from the list?`)) remove.mutate(item.id)
                    }}
                  >
                    Remove
                  </button>
                </li>
              )
            })}
          </ul>
        </>
      )}

      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <Input aria-label="Deliverable" placeholder="e.g. 20 Polaroids" value={title} onChange={(event) => setTitle(event.target.value)} className="flex-1" />
        <Select aria-label="Kind" value={kind} onChange={(event) => setKind(event.target.value as Deliverable['kind'])} className="w-24">
          {(Object.keys(DELIVERABLE_KIND_LABEL) as Array<Deliverable['kind']>).map((key) => (
            <option key={key} value={key}>
              {DELIVERABLE_KIND_LABEL[key]}
            </option>
          ))}
        </Select>
        <Button type="submit" size="sm" disabled={!title.trim()}>
          Add
        </Button>
      </form>
    </div>
  )
}
